import React, { useEffect, useState, useRef, useMemo } from "react";
// ANT DESIGN COMPONENTS
import { Button, Popover, notification } from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faCircleInfo } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector } from "react-redux";
// CUSTOM HOOKS
import usePostCommand from "../../../customHooks/POST_requests/usePostCommand";
import useGetAllMessages from "../../../customHooks/GET_requests/useGetAllMessages";
// COMPONENTS
import ActionButtonPopover from "./ActionButtonPopover";
import GlobalAlert2 from "../../home/GlobalAlert2";
// FLAT BUFFERS
import { ElevatorControl } from "../../../flatbuffers/generated/control/elevator-control";
// INTERFACES
import { RootState } from "../../../app/store";

export default function ActionButtonToggle({
	validStates,
	toggleValidStates,
	label,
	toggleLabel,
	messageId,
	toggleMessageId,
	colorAction,
	colorToggleAction,
	carService,
}: {
	validStates: string[];
	toggleValidStates: string[];
	label: string;
	toggleLabel: string;
	messageId: string;
	toggleMessageId: string;
	colorAction: string;
	colorToggleAction: string;
	carService: number;
}) {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [localCarService, setLocalCarService] = useState<number>(0);
	const [lastCommandSent, setLastCommandSent] = useState<string>("ON");
	const localCarServiceRef = useRef(localCarService);

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const { theme, userLogged } = useSelector((state: RootState) => state.home);

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */

	// ************************************************ */
	// CUSTOM HOOKS *********************************** */
	const { triggerPostCommand, isLoadingPostCommand, isSuccessPostCommand, isErrorPostCommand } = usePostCommand();
	const { messages } = useGetAllMessages();

	// ************************************************ */
	// MEMOIZED VALUES ******************************** */
	const messageEnum = useMemo(() => {
		const msg = messages.find(m => m.id === messageId);
		return msg?.short_message ?? "";
	}, [messages, messageId]);

	const toggleMessageEnum = useMemo(() => {
		const msg = messages.find(m => m.id === toggleMessageId);
		return msg?.short_message ?? "";
	}, [messages, toggleMessageId]);

	const validStatesNumbers = useMemo(() =>
		validStates.map(id => {
			const msg = messages.find(m => m.id === id);
			return msg?.enum_id ?? 0;
		}).filter(enumId => enumId !== 0)
		, [messages, validStates]);

	const toggleValidStatesNumbers = useMemo(() =>
		toggleValidStates.map(id => {
			const msg = messages.find(m => m.id === id);
			return msg?.enum_id ?? 0;
		}).filter(enumId => enumId !== 0)
		, [messages, toggleValidStates]);

	const messageEnumId = useMemo(() => {
		const msg = messages.find(m => m.id === messageId);
		if (!msg?.short_message) return 0;
		// Map short_message to ElevatorControl enum
		const enumValue = ElevatorControl[msg.short_message as keyof typeof ElevatorControl];
		return enumValue !== undefined ? enumValue : 0;
	}, [messages, messageId]);

	const toggleMessageEnumId = useMemo(() => {
		const msg = messages.find(m => m.id === toggleMessageId);
		if (!msg?.short_message) return 0;
		// Map short_message to ElevatorControl enum
		const enumValue = ElevatorControl[msg.short_message as keyof typeof ElevatorControl];
		return enumValue !== undefined ? enumValue : 0;
	}, [messages, toggleMessageId]);

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const onClickToggleOn = () => {
		setLastCommandSent("ON");
		triggerPostCommand({
			floor: 0,
			command: messageEnumId,
			liftType: "ELEVATOR",
			active: true,
		});
	};

	const onClickToggleOff = () => {
		setLastCommandSent("OFF");
		triggerPostCommand({
			floor: 0,
			command: toggleMessageEnumId,
			liftType: "ELEVATOR",
			active: true,
		});
	};

	const launchTimeoutMessage = () => {
		if (userLogged.command_notifications) {
			notification.open({
				message: <span className='generalStyles__warning'>Timeout</span>,
				description: <span>{label}</span>,
				placement: "top",
				className: `monitoring__notification__${theme}`,
				icon: (
					<FontAwesomeIcon
						icon={faCheckCircle}
						fontSize={18}
						className='monitoring__modalSettings__icon generalStyles__warning'
					/>
				),
			});
		}
	};

	const launchSuccessMessage = (label_: string) => {
		if (userLogged.command_notifications) {
			notification.open({
				message: <span className='generalStyles__success'>Success</span>,
				description: <span>{label_}</span>,
				placement: "top",
				className: `monitoring__notification__${theme}`,
				icon: (
					<FontAwesomeIcon
						icon={faCheckCircle}
						fontSize={18}
						className='monitoring__modalSettings__icon generalStyles__success'
					/>
				),
			});
		}
	};

	const checkCarServiceState = () => {
		const currentLocalCarService = localCarServiceRef.current; // Get the latest value
		// Check the state of the variable
		if (lastCommandSent === "ON" && validStatesNumbers.includes(currentLocalCarService)) {
			launchSuccessMessage(label);
			return true;
		}
		if (lastCommandSent === "OFF" && toggleValidStatesNumbers.includes(currentLocalCarService)) {
			launchSuccessMessage(toggleLabel);
			return true;
		}
		return false;
	};

	const checkVariableState = (maxAttempts = 5, interval = 1000) => {
		let attempts = 0;
		const intervalId = setInterval(() => {
			attempts += 1;
			if (checkCarServiceState()) {
				clearInterval(intervalId); // Stop checking
			} else if (attempts >= maxAttempts) {
				clearInterval(intervalId); // Stop checking
				launchTimeoutMessage();
			}
		}, interval);
	};

	// eslint-disable-next-line arrow-body-style
	const formatCamelCaseLabel = (value: string): string => {
		return value.replace(/([a-z])([A-Z])/g, "$1 $2");
	};

	// ************************************************ */
	// USE EFFECT ************************************* */
	useEffect(() => {
		if (isSuccessPostCommand) {
			checkVariableState();
		}
	}, [isSuccessPostCommand]);

	useEffect(() => {
		setLocalCarService(carService);
	}, [carService]);

	useEffect(() => {
		localCarServiceRef.current = localCarService;
	}, [localCarService]);

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<div className='generalStyles__width100 generalStyles__flexCenter mb-4'>
			<div>
				<div className='generalStyles__flex'>
					<div style={{ width: "30px", paddingTop: "30px", marginLeft: "-15px" }}>
						<Popover
							content={
								<ActionButtonPopover
									label={label}
									toggleLabel={toggleLabel}
									validStates={validStates}
									toggleValidStates={toggleValidStates}
									type='TOGGLE'
									timer1={0}
									timer2={0}
									message={messageEnum}
									toggleMessage={toggleMessageEnum}
									color={colorAction}
									toggleColor={colorToggleAction}
								/>
							}
							title="TOGGLE"
						>
							<FontAwesomeIcon
								icon={faCircleInfo}
								fontSize={18}
								className='generalStyles__success generalStyles__mrFix'
							/>
						</Popover>
					</div>
					<Button
						className={
							validStatesNumbers.includes(localCarService) || isLoadingPostCommand
								? ""
								: "buttonStyle__basicButtonAction"
						}
						disabled={validStatesNumbers.includes(localCarService) || isLoadingPostCommand}
						loading={isLoadingPostCommand && lastCommandSent === "ON"}
						onClick={onClickToggleOn}
						style={{ backgroundColor: `${colorAction}`, width: "210px" }}
					>
						<span className='generalStyles__textEllipsis' style={{ marginLeft: "5px" }}>
							{formatCamelCaseLabel(messageEnum)}
						</span>
					</Button>
				</div>
				<div className='generalStyles__flex' style={{ marginTop: "-13px" }}>
					<div style={{ width: "15px" }} />
					<Button
						className={
							toggleValidStatesNumbers.includes(localCarService) || isLoadingPostCommand
								? ""
								: "buttonStyle__basicButtonAction"
						}
						style={{ backgroundColor: `${colorToggleAction}`, width: "210px" }}
						disabled={toggleValidStatesNumbers.includes(localCarService) || isLoadingPostCommand}
						loading={isLoadingPostCommand && lastCommandSent === "OFF"}
						onClick={onClickToggleOff}
					>
						<span className='generalStyles__textEllipsis' style={{ marginLeft: "5px" }}>
							{formatCamelCaseLabel(toggleMessageEnum)}
						</span>
					</Button>
				</div>
			</div>
			{/** ---------------------------------------------------- */}
			{/** GLOBAL ALERT */}
			<GlobalAlert2 isError={isErrorPostCommand} isSuccess={false} requestType='POST' error='Error' name='Command' />
		</div>
	);
}
